import type { Lang } from './languages.js';
import type { LanguageRequirement } from './modes.js';
import type { RuleSet } from './rules.js';

/**
 * Deterministic fixed-point scoring.
 *
 *   final = base × (1 + languageBonus + speedBonus)
 *
 * Bonuses add up (never multiply each other). Everything is computed with integers (BigInt)
 * and rounded once, at the end, to cents (half-up). The same function runs on client and
 * server, so the displayed and the official score are identical.
 */
export interface ScoredWord {
  key: string;
  lang: Lang;
  points: number;
}

export interface LanguageCount {
  lang: Lang;
  words: number;
  points: number;
}

export interface ScoreBreakdown {
  basePoints: number;
  perLanguage: LanguageCount[];
  language: {
    kind: LanguageRequirement['kind'];
    contract: LanguageRequirement['contract'];
    quotaPerLanguage: number;
    fulfilled: boolean;
    /** Bonus actually applied, in basis points. */
    appliedBp: number;
    /** Bonus that the requirement offers when fulfilled. */
    offeredBp: number;
  };
  speed: {
    eligible: boolean;
    targetReached: boolean;
    /** Official time in ms (server clock) when the target was reached; null otherwise. */
    officialTimeMs: number | null;
    /** Applied speed bonus as a rational number of basis points: numeratorBp / denominator. */
    bonusBpNumerator: number;
    bonusBpDenominator: number;
  };
  /** Final score in hundredths (e.g. 12350 = 123.50). */
  totalCents: number;
}

export interface ScoreInput {
  words: readonly ScoredWord[];
  languages: readonly Lang[];
  requirement: LanguageRequirement;
  /** Whether the mode awards a speed bonus (Duel). */
  speedBonus: boolean;
  /** Target words (Duel) or null. */
  targetWords: number | null;
  /** Server-measured time at which the round ended because the target was reached. */
  targetReachedAtMs: number | null;
  timeLimitMs: number;
  rules: RuleSet;
}

export function countByLanguage(
  words: readonly ScoredWord[],
  languages: readonly Lang[],
): LanguageCount[] {
  return languages.map((lang) => {
    let n = 0;
    let p = 0;
    for (const w of words) {
      if (w.lang === lang) {
        n++;
        p += w.points;
      }
    }
    return { lang, words: n, points: p };
  });
}

export function isLanguageRequirementFulfilled(
  requirement: LanguageRequirement,
  perLanguage: readonly LanguageCount[],
): boolean {
  if (requirement.kind === 'none' || requirement.bonusBp === 0) return false;
  if (requirement.quotaPerLanguage === 0) return true; // Specialist: unconditional.
  return perLanguage.every((c) => c.words >= requirement.quotaPerLanguage);
}

/** Rounds num/den (both ≥ 0) half-up. */
function divRoundHalfUp(num: bigint, den: bigint): bigint {
  return (2n * num + den) / (2n * den);
}

export function computeScore(input: ScoreInput): ScoreBreakdown {
  const { words, languages, requirement, rules } = input;
  const basePoints = words.reduce((s, w) => s + w.points, 0);
  const perLanguage = countByLanguage(words, languages);
  const fulfilled = isLanguageRequirementFulfilled(requirement, perLanguage);
  const langBp = fulfilled ? requirement.bonusBp : 0;

  const T = input.timeLimitMs;
  const targetReached =
    input.targetWords !== null &&
    words.length >= input.targetWords &&
    input.targetReachedAtMs !== null;
  const speedEligible = input.speedBonus && targetReached;
  let t = input.targetReachedAtMs ?? T;
  if (t < 0) t = 0;
  if (t > T) t = T;
  // speed bonus (bp) = speedBonusMaxBp × (T − t) / T  — kept as a rational number.
  const speedNum = speedEligible ? rules.speedBonusMaxBp * (T - t) : 0;
  const speedDen = T;

  // cents = base × 100 × (1 + langBp/10000 + speedNum/(speedDen×10000))
  //       = base × ((10000 + langBp) × T + speedNum) / (100 × T)
  const num = BigInt(basePoints) * (BigInt(10000 + langBp) * BigInt(speedDen) + BigInt(speedNum));
  const den = 100n * BigInt(speedDen);
  const totalCents = Number(divRoundHalfUp(num, den));

  return {
    basePoints,
    perLanguage,
    language: {
      kind: requirement.kind,
      contract: requirement.contract,
      quotaPerLanguage: requirement.quotaPerLanguage,
      fulfilled,
      appliedBp: langBp,
      offeredBp: requirement.bonusBp,
    },
    speed: {
      eligible: speedEligible,
      targetReached,
      officialTimeMs: targetReached ? t : null,
      bonusBpNumerator: speedNum,
      bonusBpDenominator: speedDen,
    },
    totalCents,
  };
}

/** Formats cents as a decimal string with exactly two decimals (locale formatting is UI's job). */
export function formatCents(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

/** Speed bonus percentage rounded to 2 decimals, for display only. */
export function speedBonusPercent(b: ScoreBreakdown['speed']): number {
  if (b.bonusBpDenominator === 0) return 0;
  return Math.round((b.bonusBpNumerator / b.bonusBpDenominator) as number) / 100;
}
